import { supabase } from '../lib/supabase';

/**
 * Calendar Service
 * Handles all calendar event operations with center-based filtering
 */
class CalendarService {
  /**
   * Event type color mapping
   */
  static EVENT_COLORS = {
    juma: '#4CAF50',        // Medium Green - Friday Prayer
    prayer: '#4CAF50',      // Medium Green - General Prayer
    majlis: '#000000',      // Black - Mourning/remembrance
    celebration: '#dc2626', // Red - Joyous occasions
    eid: '#dc2626',         // Red - Eid celebrations
    school: '#86efac',      // Light Green - Saturday School
    ramadan: '#fbbf24',     // Amber - Ramadan events
    muharram: '#000000',    // Black - Muharram
    amaal: '#1e40af',       // Blue - Special prayers
    lecture: '#7c3aed',     // Purple - Lectures
    community: '#FFC107',   // Yellow - Community events
    special: '#ea580c',     // Orange - Special events
    general: '#6b7280'      // Gray - General events
  };

  /**
   * Get display-friendly event type labels
   */
  static EVENT_TYPE_LABELS = {
    juma: 'Juma Prayer',
    prayer: 'Prayer',
    majlis: 'Majlis',
    celebration: 'Celebration',
    eid: 'Eid',
    school: 'Saturday School',
    ramadan: 'Ramadan',
    muharram: 'Muharram',
    amaal: 'Special Prayers',
    lecture: 'Lecture',
    community: 'Community',
    special: 'Special Event',
    general: 'General'
  };

  /**
   * Event type emoji mapping — canonical visual identity per category
   * Matches the admin portal's EventDialog category dropdown
   */
  static EVENT_TYPE_EMOJIS = {
    juma: '🕌',
    prayer: '🤲',
    majlis: '📿',
    celebration: '🎉',
    eid: '🎉',
    school: '📚',
    ramadan: '🌙',
    muharram: '🏴',
    amaal: '🤲',
    lecture: '🎤',
    community: '👥',
    special: '✨',
    general: '📅'
  };

  /**
   * Fetch all events for a specific center
   * @param {string} centerId - The center's UUID
   * @param {Date} startDate - Start of date range
   * @param {Date} endDate - End of date range
   */
  async getEvents(centerId, startDate = null, endDate = null) {
    try {
      if (!centerId) {
        throw new Error('Center ID is required');
      }

      let query = supabase
        .from('calendar_events')
        .select('*')
        .eq('center_id', centerId)
        .eq('is_active', true);

      // Add date range filter if provided
      if (startDate) {
        query = query.gte('start_date', this.formatDate(startDate));
      }
      if (endDate) {
        query = query.lte('start_date', this.formatDate(endDate));
      }

      // Order by date and time
      query = query.order('start_date', { ascending: true })
        .order('start_time', { ascending: true });

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching calendar events:', error);
        throw error;
      }

      // Process events to add display properties
      const processedEvents = (data || []).map(event => ({
        ...event,
        color: CalendarService.EVENT_COLORS[event.event_type] || CalendarService.EVENT_COLORS.general,
        typeLabel: CalendarService.EVENT_TYPE_LABELS[event.event_type] || 'Event',
        displayTime: this.formatTime(event.start_time),
        displayDate: this.formatDisplayDate(event.start_date),
        isToday: this.isToday(event.start_date),
        isPast: this.isPastEvent(event.start_date),
        isFuture: this.isFutureEvent(event.start_date),
        isUpcoming: this.isEventUpcoming(event),
        isStale: this.isEventStale(event),
        isLaterToday: this.isEventLaterToday(event),
        canSponsor: this.canSponsorEvent(event)
      }));

      return processedEvents;
    } catch (error) {
      console.error('Calendar service error:', error);
      throw error;
    }
  }

  /**
   * Get events for a specific month
   * @param {string} centerId - The center's UUID
   * @param {number} year - Year (e.g., 2025)
   * @param {number} month - Month (1-12)
   */
  async getMonthEvents(centerId, year, month) {
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0); // Last day of month
    
    return this.getEvents(centerId, startDate, endDate);
  }

  /**
   * Get events for current week
   * @param {string} centerId - The center's UUID
   */
  async getWeekEvents(centerId) {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay()); // Sunday
    startOfWeek.setHours(0, 0, 0, 0);
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6); // Saturday
    endOfWeek.setHours(23, 59, 59, 999);
    
    return this.getEvents(centerId, startOfWeek, endOfWeek);
  }

  /**
   * Get upcoming events (next 30 days)
   * @param {string} centerId - The center's UUID
   * @param {number} days - Number of days to look ahead (default 30)
   */
  async getUpcomingEvents(centerId, days = 30) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const futureDate = new Date(today);
    futureDate.setDate(today.getDate() + days);
    
    return this.getEvents(centerId, today, futureDate);
  }

  /**
   * Get event count for a specific month
   * @param {string} centerId - The center's UUID
   * @param {number} year - Year
   * @param {number} month - Month (1-12)
   */
  async getMonthEventCount(centerId, year, month) {
    try {
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0);
      
      const { count, error } = await supabase
        .from('calendar_events')
        .select('*', { count: 'exact', head: true })
        .eq('center_id', centerId)
        .eq('is_active', true)
        .gte('start_date', this.formatDate(startDate))
        .lte('start_date', this.formatDate(endDate));

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error getting event count:', error);
      return 0;
    }
  }

  /**
   * Group events by date for list view
   * @param {Array} events - Array of events
   */
  groupEventsByDate(events) {
    const grouped = {};
    
    events.forEach(event => {
      const dateKey = event.start_date;
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: dateKey,
          displayDate: this.formatDisplayDate(dateKey),
          dayName: this.getDayName(dateKey),
          isToday: this.isToday(dateKey),
          isPast: this.isPastEvent(dateKey),
          events: []
        };
      }
      grouped[dateKey].events.push(event);
    });
    
    // Convert to array and sort by date
    return Object.values(grouped).sort((a, b) => 
      new Date(a.date + 'T00:00:00') - new Date(b.date + 'T00:00:00')
    );
  }

  /**
   * Check if event can be sponsored
   * @param {Object} event - Event object
   */
  canSponsorEvent(event) {
    // Don't allow sponsorship for Juma prayers
    if (event.event_type === 'juma') return false;
    
    // Only allow sponsorship for today's or future events
    const eventDate = new Date(event.start_date + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    
    return eventDate >= today;
  }

  /**
   * Format date for database queries (YYYY-MM-DD)
   */
  formatDate(date) {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Format time for display (e.g., "7:30 PM")
   */
  formatTime(timeString) {
    if (!timeString) return '';
    
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
    
    return `${displayHour}:${minutes} ${ampm}`;
  }

  /**
   * Format date for display (e.g., "September 9, 2025")
   */
  formatDisplayDate(dateString) {
    const date = new Date(dateString + 'T00:00:00'); // Add time to avoid timezone issues
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }

  /**
   * Get day name from date
   */
  getDayName(dateString) {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  }

  /**
   * Check if date is today
   */
  isToday(dateString) {
    const date = new Date(dateString + 'T00:00:00');
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }

  /**
   * Check if event is in the past
   */
  isPastEvent(dateString) {
    const eventDate = new Date(dateString + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    return eventDate < today;
  }

  /**
   * Check if event is in the future
   */
  isFutureEvent(dateString) {
    const eventDate = new Date(dateString + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    return eventDate > today;
  }

  /**
   * Check if event is upcoming (hasn't ended yet, including today's events)
   * @param {Object} event - Event object with start_date, start_time, end_date, end_time
   * @returns {boolean} True if event is upcoming or happening today
   */
  isEventUpcoming(event) {
    const now = new Date();
    
    // Calculate event end time
    const endDate = event.end_date || event.start_date;
    const endTime = event.end_time || '23:59:59';
    const eventEnd = new Date(endDate + 'T' + endTime);
    
    // Event is upcoming if it hasn't ended yet
    return eventEnd > now;
  }

  /**
   * Check if event is stale (3 hours after start time)
   * @param {Object} event - Event object with start_date and start_time
   * @returns {boolean} True if more than 3 hours have passed since event start
   */
  isEventStale(event) {
    const now = new Date();
    
    // Calculate 3 hours after event start
    const startTime = event.start_time || '00:00:00';
    const eventStart = new Date(event.start_date + 'T' + startTime);
    const disappearTime = new Date(eventStart.getTime() + (3 * 60 * 60 * 1000));
    
    // Event is stale if current time is past the 3-hour mark
    return now > disappearTime;
  }

  /**
   * Check if event is happening later today
   * @param {Object} event - Event object
   * @returns {boolean} True if event is today but hasn't started yet
   */
  isEventLaterToday(event) {
    const now = new Date();
    const eventStart = new Date(event.start_date + 'T' + (event.start_time || '00:00:00'));
    
    // Check if same day
    const isToday = now.toDateString() === eventStart.toDateString();
    
    // Check if hasn't started yet
    const notStarted = eventStart > now;
    
    return isToday && notStarted;
  }

  /**
   * Get calendar grid data for month view
   * @param {Array} events - Array of events for the month
   * @param {number} year - Year
   * @param {number} month - Month (1-12)
   */
  getCalendarGrid(events, year, month) {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const startDate = new Date(firstDay);
    
    // Adjust to start from Sunday
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const grid = [];
    const eventsByDate = {};
    
    // Group events by date
    events.forEach(event => {
      if (!eventsByDate[event.start_date]) {
        eventsByDate[event.start_date] = [];
      }
      eventsByDate[event.start_date].push(event);
    });
    
    // Build up to 6 weeks grid, but stop if the last week only contains next month
    for (let week = 0; week < 6; week++) {
      const weekDays = [];
      let hasCurrentMonthDay = false;
      
      for (let day = 0; day < 7; day++) {
        const currentDate = new Date(startDate);
        currentDate.setDate(startDate.getDate() + (week * 7) + day);
        
        const dateStr = this.formatDate(currentDate);
        const dayNumber = currentDate.getDate();
        const isCurrentMonth = currentDate.getMonth() === month - 1;
        const isToday = this.isToday(dateStr);
        const isPast = this.isPastEvent(dateStr);
        
        // Track if this week has any current month days
        if (isCurrentMonth) {
          hasCurrentMonthDay = true;
        }
        
        weekDays.push({
          date: dateStr,
          dayNumber,
          isCurrentMonth,
          isToday,
          isPast,
          events: eventsByDate[dateStr] || []
        });
      }
      
      // Only add this week if it contains at least one day from the current month
      if (hasCurrentMonthDay) {
        grid.push(weekDays);
      } else {
        // Stop building weeks if this week has no current month days
        break;
      }
    }
    
    return grid;
  }

  /**
   * Get all events for a center (no date filtering)
   * @param {string} centerId - The center's UUID
   */
  async getAllEvents(centerId) {
    try {
      if (!centerId) {
        throw new Error('Center ID is required');
      }

      const { data, error } = await supabase
        .from('calendar_events')
        .select('*')
        .eq('center_id', centerId)
        .eq('is_active', true)
        .order('start_date', { ascending: true })
        .order('start_time', { ascending: true });

      if (error) {
        console.error('Error fetching all calendar events:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Calendar service error:', error);
      return [];
    }
  }

  /**
   * Calculate annual event projection based on historical data
   * @param {string} centerId - The center's UUID
   * @returns {Object} Projection data including count and confidence level
   */
  async calculateAnnualProjection(centerId) {
    try {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;
      
      // Get all events for the center
      const allEvents = await this.getAllEvents(centerId);
      
      if (!allEvents || allEvents.length === 0) {
        return {
          projection: null,
          confidence: 'no_data',
          method: 'no_data',
          message: 'No historical event data available'
        };
      }

      // Group events by year and month
      const eventsByYear = {};
      const eventsByMonth = {};
      
      allEvents.forEach(event => {
        const date = new Date(event.start_date);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const monthKey = `${year}-${String(month).padStart(2, '0')}`;
        
        // Group by year
        if (!eventsByYear[year]) {
          eventsByYear[year] = [];
        }
        eventsByYear[year].push(event);
        
        // Group by month
        if (!eventsByMonth[monthKey]) {
          eventsByMonth[monthKey] = [];
        }
        eventsByMonth[monthKey].push(event);
      });

      // Check if we have last year's full data
      const lastYear = currentYear - 1;
      if (eventsByYear[lastYear]) {
        const lastYearEvents = eventsByYear[lastYear];
        const lastYearMonths = new Set();
        
        lastYearEvents.forEach(event => {
          const month = new Date(event.start_date).getMonth() + 1;
          lastYearMonths.add(month);
        });
        
        // If we have data for 10+ months of last year, use that
        if (lastYearMonths.size >= 10) {
          return {
            projection: lastYearEvents.length,
            confidence: 'high',
            method: 'last_year_actual',
            message: `Based on ${lastYear}'s actual data`,
            actualCount: lastYearEvents.length,
            year: currentYear
          };
        }
      }

      // Calculate based on available months
      const monthsWithData = Object.keys(eventsByMonth).length;
      const totalEvents = allEvents.length;
      
      // Count distinct year-month combinations to avoid timezone issues
      const distinctMonths = new Set();
      allEvents.forEach(event => {
        // Parse date safely - treat as local date string
        const dateParts = event.start_date.split('-');
        const year = parseInt(dateParts[0]);
        const month = parseInt(dateParts[1]);
        distinctMonths.add(`${year}-${month}`);
      });
      
      // Use the count of distinct months as the span
      const monthsSpan = distinctMonths.size;
      
      console.log('[calculateAnnualProjection] Distinct months:', Array.from(distinctMonths));
      console.log('[calculateAnnualProjection] Month span:', monthsSpan, 'Total events:', totalEvents);
      
      // Calculate average events per month
      const avgEventsPerMonth = totalEvents / monthsSpan;
      
      // Apply conservative clipping based on months of data available
      let clippingFactor, confidence, method, message;
      
      if (monthsSpan === 1) {
        clippingFactor = 0.80; // 80% for single month
        confidence = 'high'; // Set to high since we're being conservative
        method = 'single_month_conservative';
        message = `Based on 1 month of data (80% conservative estimate)`;
      } else if (monthsSpan === 2) {
        clippingFactor = 0.85; // 85% for 2 months
        confidence = 'high';
        method = 'two_month_conservative';
        message = `Based on ${monthsSpan} months of data (85% conservative estimate)`;
      } else if (monthsSpan === 3) {
        clippingFactor = 0.90; // 90% for 3 months
        confidence = 'high';
        method = 'three_month_conservative';
        message = `Based on ${monthsSpan} months of data (90% conservative estimate)`;
      } else if (monthsSpan >= 4 && monthsSpan <= 5) {
        clippingFactor = 0.95; // 95% for 4-5 months
        confidence = 'high';
        method = 'multi_month_conservative';
        message = `Based on ${monthsSpan} months of data (95% conservative estimate)`;
      } else {
        clippingFactor = 1.0; // 100% for 6+ months (no clipping)
        confidence = 'high';
        method = 'multi_month_average';
        message = `Based on ${monthsSpan} months of historical data`;
      }
      
      // Calculate projection with clipping factor
      const projection = Math.round(avgEventsPerMonth * 12 * clippingFactor);
      
      console.log('[calculateAnnualProjection] Calculation details:');
      console.log('  - Average per month:', avgEventsPerMonth);
      console.log('  - Clipping factor:', clippingFactor);
      console.log('  - Formula: ', avgEventsPerMonth, '× 12 ×', clippingFactor, '=', avgEventsPerMonth * 12 * clippingFactor);
      console.log('  - Final projection:', projection);

      return {
        projection,
        confidence,
        method,
        message,
        monthsAnalyzed: monthsSpan,
        avgPerMonth: avgEventsPerMonth.toFixed(1),
        clippingFactor: Math.round(clippingFactor * 100),
        year: currentYear
      };

    } catch (error) {
      console.error('Error calculating annual projection:', error);
      return {
        projection: null,
        confidence: 'error',
        method: 'error',
        message: 'Unable to calculate projection'
      };
    }
  }
}

// Export singleton instance
const calendarService = new CalendarService();
export default calendarService;